import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MaterialReactTable } from 'material-react-table';
import { 
  FiSearch, 
  FiX,
  FiCheck,
  FiAlertTriangle,
  FiEye,
  FiFilter,
  FiInfo,
  FiClock,
  FiUpload,
  FiChevronDown,
  FiDownload,
  FiFileText,
  FiActivity,
  FiCode
} from 'react-icons/fi';
import LaboratoryOrdersService from '../../../../PatientTreatmentDetails/services/laboratoryOrdersService';
import TopHeader from "../../../../common/Header/TopHeader";
import PatientMedicalClinicalData from '../../../../Patient/listPrincipalComponent/TableComponent/PatientMedicalClinicalData';
import PatientManagement from '../../../../common/Menu/PatientManagement';
import AddNewLaboratoryOrder from 'components/PatientTreatmentDetails/Components/Laboratory/AddNewLaboratoryOrder';

const ResultTreatments = ({ onCountUpdate, componentName = 'ResultTreatments' }) => {
  const location = useLocation();
  
  // **OBTENER DATOS DEL PACIENTE DESDE NAVIGATION STATE**
  const patientData = location.state;
  const patientId = patientData?.patientId;

  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [alertInfo, setAlertInfo] = useState(null);
  const [showFilterOffcanvas, setShowFilterOffcanvas] = useState(false);
  const [showViewOffcanvas, setShowViewOffcanvas] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [showAddOffcanvas, setShowAddOffcanvas] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Estados para subir resultados
const [showResultsOffcanvas, setShowResultsOffcanvas] = useState(false);
const [resultsData, setResultsData] = useState({});
const [isUploadingResults, setIsUploadingResults] = useState(false);
const [resultsErrors, setResultsErrors] = useState({});
const [uploadError, setUploadError] = useState('');
const [uploadSuccess, setUploadSuccess] = useState('');
const [generalNotes, setGeneralNotes] = useState('');
  
  // Filter state
  const [filters, setFilters] = useState({
    testType: '',
    severity: '',
    completedDate: '',
    endDate: '',
    status: '',
    abnormalOnly: false
  });

  // **FUNCIÓN PRINCIPAL PARA CARGAR DATOS**
  const loadResults = useCallback(async () => {
    if (!patientId) {
      console.log('No patientId provided, component will show empty state');
      setResults([]);
      onCountUpdate && onCountUpdate(componentName, 0);
      return;
    }
    
    setIsLoading(true);
    try {
      console.log('Loading laboratory results for patient:', patientId);
      const response = await LaboratoryOrdersService.getPatientOrders(patientId);
      console.log('Raw API response:', response);
      
      let ordersData = [];
      
      if (response.data) {
        ordersData = Array.isArray(response.data) ? response.data : [response.data];
      } else if (Array.isArray(response)) {
        ordersData = response;
      } else if (response.orders) {
        ordersData = Array.isArray(response.orders) ? response.orders : [response.orders];
      }

     // **MOSTRAR TODAS LAS ÓRDENES COMO EN LaboratoryTreatments**
const normalizedResults = ordersData.map(order => {
  console.log('Processing order:', order);
  
  const testInfo = extractTestInfoFromOrder(order);
  
  // Handle patient information (could be populated or just ID)
  const patientInfo = typeof order.patientId === 'object' && order.patientId 
    ? { id: order.patientId._id, name: order.patientId.name }
    : { id: order.patientId || patientId, name: patientData.patientName || 'Unknown Patient' };

  // Handle createdBy information (could be populated or just ID)
  const createdByInfo = typeof order.createdBy === 'object' && order.createdBy
    ? { id: order.createdBy._id, name: order.createdBy.name, email: order.createdBy.email }
    : { id: order.createdBy || order.orderedById, name: order.orderedByName || 'Unknown', email: '' };

  // Count abnormal results
  const abnormalCount = countAbnormalResults(order);
  const totalParameters = countTotalParameters(order);

  return {
    // Primary identifiers
    result_id: order._id || order.order_id || order.id,
    order_number: order.orderNumber || order.order_number || `LAB-${Date.now()}`,
    sample_id: order.sample_id || order.specimen_id || null,
    
    // Test information
    test_type: testInfo.categories,
    test_name: testInfo.testNames,
    test_count: testInfo.testCount,
    
    // Result metrics
    total_parameters: totalParameters,
    abnormal_count: abnormalCount,
    has_abnormal: abnormalCount > 0,
    severity: abnormalCount > 0 ? (abnormalCount >= 3 ? 'high' : 'medium') : 'normal',
    
    // Status and priority
    status: order.status || 'pending', // Mantener el status real
    priority: order.priority || (order.isUrgent ? 'urgent' : 'routine'),
    is_urgent: order.isUrgent || order.is_urgent || false,
    
    // Dates
    order_date: order.createdAt || order.order_date || order.created_date || order.orderDate,
    completed_date: order.completed_date || order.result_date,
    last_updated: order.updatedAt || order.last_updated || order.modified_date,
    
    // Personnel
    ordered_by: createdByInfo.name,
    ordered_by_id: createdByInfo.id,
    processed_by: order.processed_by || order.technician || null,
    reviewed_by: order.reviewed_by || order.pathologist || null,
    
    // Results and samples
    results: order.results || [],
    results_notes: order.results_notes || order.observations || '',
    
    // Patient info
    patient_id: patientInfo.id,
    patient_name: patientInfo.name,
    
    // Preserve original for detailed view
    _original: order
  };
});

      console.log('Normalized results:', normalizedResults);
      
      // **ACTUALIZAR CONTADOR SOLO SI CAMBIÓ**
      if (results.length !== normalizedResults.length) {
        console.log(`📊 ${componentName}: Contador cambió de ${results.length} a ${normalizedResults.length}`);
        onCountUpdate && onCountUpdate(componentName, normalizedResults.length);
      }
      
      setResults(normalizedResults);
    } catch (error) {
      console.error('Error loading laboratory results:', error);
      setResults([]);
      if (results.length !== 0) {
        onCountUpdate && onCountUpdate(componentName, 0);
      }
    } finally {
      setIsLoading(false);
    }
  }, [patientId, results.length, onCountUpdate, componentName, patientData]);

  // Helper function to extract test info from order
  const extractTestInfoFromOrder = (order) => {
    if (order.selectedTests && Array.isArray(order.selectedTests)) {
      const categories = order.selectedTests
        .map(test => test.category || test.testCategory)
        .filter(category => category && category.trim() !== '')
        .filter((category, index, arr) => arr.indexOf(category) === index);
      
      const testNames = order.selectedTests
        .map(test => test.testName || test.test_name || test.name)
        .filter(name => name && name.trim() !== '');
      
      return {
        categories: categories.length > 0 ? categories.join(', ') : (order.test_type || order.type || 'Unknown'),
        testNames: testNames.length > 0 ? testNames.join(', ') : (order.test_name || order.name || 'Unknown Test'),
        testCount: order.selectedTests.length
      };
    }
    
    // Fallback for legacy structure
    return {
      categories: order.test_type || order.type || 'Unknown',
      testNames: order.test_name || order.name || order.description || 'Unknown Test',
      testCount: 1
    };
  };

  // Helper function to count abnormal results
  const countAbnormalResults = (order) => {
    let abnormalCount = 0;
    
    // Check structured results
    if (order.results && Array.isArray(order.results)) {
      order.results.forEach(testResult => {
        if (testResult.parameters && Array.isArray(testResult.parameters)) {
          abnormalCount += testResult.parameters.filter(param => param.isAbnormal).length;
        }
      });
    }
    
    // Check selectedTests results
    if (order.selectedTests && Array.isArray(order.selectedTests)) {
      order.selectedTests.forEach(test => {
        if (test.selectedParameters && Array.isArray(test.selectedParameters)) {
          abnormalCount += test.selectedParameters.filter(param => param.isAbnormal).length;
        }
      });
    }
    
    return abnormalCount;
  };

  // Helper function to count total parameters
  const countTotalParameters = (order) => {
    let totalCount = 0;
    
    // Count from structured results
    if (order.results && Array.isArray(order.results)) {
      order.results.forEach(testResult => {
        if (testResult.parameters && Array.isArray(testResult.parameters)) {
          totalCount += testResult.parameters.length;
        }
      });
    } else if (order.selectedTests && Array.isArray(order.selectedTests)) {
      order.selectedTests.forEach(test => {
        if (test.selectedParameters && Array.isArray(test.selectedParameters)) {
          totalCount += test.selectedParameters.length;
        }
      });
    }
    
    return totalCount;
  };

  // **FUNCIÓN SEPARADA PARA CARGAR ESTADÍSTICAS**
  const loadResultsStats = useCallback(async () => {
    if (!patientId || results.length === 0) return;
    
    try {
      const abnormalCount = results.filter(result => result.has_abnormal).length;
      const criticalCount = results.filter(result => 
        result.severity === 'high' || result.abnormal_count >= 5
      ).length;
      
      if (abnormalCount > 0 || criticalCount > 0) {
        setAlertInfo({
          alert_type: 'laboratory_results',
          message: `${abnormalCount} results with abnormal values${criticalCount > 0 ? `, ${criticalCount} critical` : ''}`,
          priority: criticalCount > 0 ? 'high' : 'medium',
          requires_action: criticalCount > 0
        });
      } else {
        setAlertInfo(null);
      }
    } catch (error) {
      console.error('Error loading results stats:', error);
      setAlertInfo(null);
    }
  }, [patientId, results]);

  const applyFilters = useCallback(() => {
  console.log('🔄 Aplicando filtros y ordenamiento...');
  
  let filtered = [...results];

  // Aplicar filtro de búsqueda global
  if (globalFilter) {
    filtered = filtered.filter(result => 
      (result.test_name || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
      (result.test_type || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
      (result.order_number || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
      (result.sample_id || '').toLowerCase().includes(globalFilter.toLowerCase())
    );
  }

  // Filtrar por tipo de test
  if (filters.testType) {
    filtered = filtered.filter(result => 
      (result.test_type || '').toLowerCase().includes(filters.testType.toLowerCase())
    );
  }

  // MODIFICAR FILTRO DE STATUS - hacer que solo filtre cuando hay un valor específico
  if (filters.status && filters.status !== '' && filters.status !== 'All') {
    const statusValue = filters.status.toLowerCase().replace(' ', '_');
    filtered = filtered.filter(result => 
      result.status.toLowerCase() === statusValue
    );
  }

    // Filtrar por severidad
    if (filters.severity) {
      filtered = filtered.filter(result => 
        result.severity === filters.severity
      );
    }

    // Filtrar solo resultados anormales
    if (filters.abnormalOnly) {
      filtered = filtered.filter(result => result.has_abnormal);
    }

    // Filtrar por fecha de completado
    if (filters.completedDate) {
      filtered = filtered.filter(result => {
        const completedDate = result.completed_date;
        return completedDate && new Date(completedDate) >= new Date(filters.completedDate);
      });
    }

    if (filters.endDate) {
      filtered = filtered.filter(result => {
        const completedDate = result.completed_date;
        return completedDate && new Date(completedDate) <= new Date(filters.endDate);
      });
    }

    // Aplicar ordenamiento
    filtered.sort((a, b) => {
      const getDate = (item) => {
        const dateValue = item.completed_date || item.order_date || new Date().toISOString();
        return new Date(dateValue);
      };
      
      switch (sortBy) {
        case 'newest':
          return getDate(b) - getDate(a);
        case 'oldest':
          return getDate(a) - getDate(b);
        case 'severity': {
          const severityOrder = { 'high': 3, 'medium': 2, 'normal': 1 };
          return (severityOrder[b.severity] || 0) - (severityOrder[a.severity] || 0);
        }
        case 'abnormal':
          return b.abnormal_count - a.abnormal_count;
        case 'alphabetical':
          const testA = (a.test_name || '').toLowerCase();
          const testB = (b.test_name || '').toLowerCase();
          return testA.localeCompare(testB);
        default:
          return 0;
      }
    });

    setFilteredResults(filtered);
}, [results, filters, globalFilter, sortBy]);

  const handleGlobalFilterChange = (filter) => {
    setGlobalFilter(filter);
  };

  const handleSortChange = useCallback((sortOption) => {
    console.log('Cambiando orden a:', sortOption);
    setSortBy(sortOption);
    setShowSortDropdown(false);
  }, []);

  // Handler to close the Add New offcanvas
const handleCloseAddOffcanvas = () => {
  setShowAddOffcanvas(false);
};

// Handler for submitting a new laboratory order
const handleSubmitNewOrder = async (newOrderData) => {
  setIsSubmitting(true);
  
  try {
    console.log('New laboratory order submitted:', newOrderData);
    
    // Close the offcanvas
    setShowAddOffcanvas(false);
    
    // Reload the results to show the new order
    await loadResults();
    
    // Show success message (optional)
    console.log('Laboratory order created successfully');
    
  } catch (error) {
    console.error('Error processing new laboratory order:', error);
    // You could add error handling here, like showing a toast notification
  } finally {
    setIsSubmitting(false);
  }
};

  const getSortDisplayText = () => {
    switch (sortBy) {
      case 'newest': return 'Newest First';
      case 'oldest': return 'Oldest First';
      case 'severity': return 'By Severity';
      case 'abnormal': return 'Most Abnormal';
      case 'alphabetical': return 'A-Z';
      default: return 'Sort by';
    }
  };

  // Table columns definition
  const columns = useMemo(() => [
    {
      accessorKey: 'order_number',
      header: 'Order #',
      size: 150,
      Cell: ({ row }) => (
        <div className="d-flex align-items-center">
          <div>
            <div className="fw-bold text-primary">
              {row.original.order_number || 'N/A'}
            </div>
            <small className="text-muted">
              {row.original.sample_id || 'No sample ID'}
            </small>
            <div className="mt-1">
              <span className="badge bg-success">
                Completed
              </span>
            </div>
          </div>
        </div>
      )
    },
    {
      accessorKey: 'test_name',
      header: 'Test Information',
      size: 200,
      Cell: ({ row }) => (
        <div>
          <div className="fw-medium text-dark">
            {row.original.test_name}
          </div>
          <div className="d-flex flex-wrap gap-1 mt-1">
            <span className="badge bg-secondary">
              {row.original.test_type}
            </span>
            {row.original.test_count > 1 && (
              <span className="badge bg-info">
                {row.original.test_count} tests
              </span>
            )}
            <span className="badge bg-primary">
              {row.original.total_parameters} params
            </span>
          </div>
        </div>
      )
    },
    {
  accessorKey: 'status',
  header: 'Status',
  size: 150,
  Cell: ({ row }) => (
    <div>
      <span className={`badge bg-${getStatusColor(row.original.status)} d-flex align-items-center gap-1`}>
        {getStatusIcon(row.original.status)}
        <span className="text-capitalize">
          {row.original.status?.replace('_', ' ') || 'Unknown'}
        </span>
      </span>
      {row.original.status === 'completed' && row.original.has_abnormal && (
        <div className="mt-1">
          <small className={`text-${row.original.severity === 'high' ? 'danger' : 'warning'}`}>
            {row.original.abnormal_count} abnormal values
          </small>
        </div>
      )}
    </div>
  )
},
    {
      accessorKey: 'completed_date',
      header: 'Completed Date',
      size: 150,
      Cell: ({ row }) => (
        <div>
          <div>{formatDate(row.original.completed_date)}</div>
          <small className="text-muted">
            by {row.original.processed_by || 'Lab'}
          </small>
        </div>
      )
    },
    {
      accessorKey: 'ordered_by',
      header: 'Ordered By',
      size: 120,
      Cell: ({ row }) => (
        <div>
          <div>{row.original.ordered_by || 'Unknown'}</div>
          <small className="text-muted">
            {formatDate(row.original.order_date)}
          </small>
        </div>
      )
    },
    {
  id: 'actions',
  header: 'Actions',
  size: 150,
  Cell: ({ row }) => (
    <div className="d-flex gap-1">
      <button
        className="btn btn-outline-primary btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleViewResult(row.original.result_id);
        }}
        title="View Results"
      >
        <FiEye />
      </button>
      {/* Agregar botón Upload Results para órdenes no completadas */}
      {(row.original.status === 'pending' || row.original.status === 'in_progress') && (
        <button
          className="btn btn-outline-success btn-sm"
          onClick={(e) => {
            e.stopPropagation();
            handleUploadResults(row.original.result_id);
          }}
          title="Upload Results"
        >
          <FiUpload />
        </button>
      )}
      <button
        className="btn btn-outline-success btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleDownloadResults(row.original.result_id);
        }}
        title="Download Results"
      >
        <FiDownload />
      </button>
    </div>
  )
}
  ], [results]);

  // **EFECTOS**
  useEffect(() => {
    console.log(`🔄 ${componentName}: useEffect triggered, patientId:`, patientId);
    if (patientId) {
      loadResults();
    }
  }, [patientId, loadResults]);

  useEffect(() => {
    if (results.length > 0) {
      loadResultsStats();
    }
  }, [results, loadResultsStats]);

  useEffect(() => {
    console.log(`📊 ${componentName}: Actualizando contador a ${results.length}`);
    onCountUpdate && onCountUpdate(componentName, results.length);
  }, [results.length, onCountUpdate, componentName]);

  useEffect(() => {
    if (results.length > 0) {
      applyFilters();
    } else {
      setFilteredResults([]);
    }
  }, [results, filters, globalFilter, sortBy, applyFilters]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown')) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Prevent body scroll when offcanvas are open
  useEffect(() => {
  if (showFilterOffcanvas || showViewOffcanvas || showAddOffcanvas || showResultsOffcanvas) {
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = '17px';
  } else {
    document.body.style.overflow = 'unset';
    document.body.style.paddingRight = '0px';
  }
  
  return () => {
    document.body.style.overflow = 'unset';
    document.body.style.paddingRight = '0px';
  };
}, [showFilterOffcanvas, showViewOffcanvas, showAddOffcanvas, showResultsOffcanvas]);

  // **HANDLERS**
  const handleViewResult = (resultId) => {
    console.log('Viewing result:', resultId);
    const result = results.find(r => r.result_id === resultId);
    if (result) {
      console.log('Found result for viewing:', result);
      setSelectedResult(result);
      setShowViewOffcanvas(true);
    } else {
      console.error('Result not found:', resultId);
    }
  };

  const handleDownloadResults = (resultId) => {
    console.log('Downloading results for:', resultId);
    // Implement download functionality
    alert('Download functionality will be implemented');
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const handleApplyFilters = () => {
    setShowFilterOffcanvas(false);
  };

  const handleClearFilters = () => {
    setFilters({
      testType: '',
      severity: '',
      completedDate: '',
      endDate: '',
      status: '',
      abnormalOnly: false
    });
  };
  const handleAddNew = () => {
    console.log('Add New button clicked - opening modal');
    setShowAddOffcanvas(true);
  };

  const handleCloseViewOffcanvas = () => {
    setShowViewOffcanvas(false);
    setSelectedResult(null);
  };

  const handleOpenFilterOffcanvas = () => {
    setShowViewOffcanvas(false);
    setShowFilterOffcanvas(true);
  };

  const handleCloseFilterOffcanvas = () => {
    setShowFilterOffcanvas(false);
  };

  // Función para abrir modal de upload de resultados
const handleUploadResults = (resultId) => {
  console.log('🔧 Opening upload results for result:', resultId);
  const result = results.find(r => r.result_id === resultId);
  
  if (result) {
    console.log('✅ Found result:', result);
    setSelectedResult(result);
    // Limpiar estados previos
    setUploadError('');
    setUploadSuccess('');
    setResultsErrors({});
    setGeneralNotes('');
    // Inicializar datos
    initializeResultsData(result);
    setShowResultsOffcanvas(true);
  } else {
    console.error('❌ Result not found:', resultId);
    alert('Result not found. Please refresh and try again.');
  }
};

// Función para inicializar datos de resultados
const initializeResultsData = (result) => {
  console.log('🔧 Initializing results data for result:', result);
  const originalOrder = result._original || result;
  const initialData = {};
  
  if (originalOrder.selectedTests && Array.isArray(originalOrder.selectedTests)) {
    originalOrder.selectedTests.forEach((test, testIndex) => {
      if (test.selectedParameters && Array.isArray(test.selectedParameters)) {
        test.selectedParameters.forEach((param, paramIndex) => {
          const key = `${testIndex}-${paramIndex}`;
          initialData[key] = {
            testIndex,
            paramIndex,
            testName: test.testName || test.test_name || test.name || 'Unknown Test',
            parameterName: param.parameterName || param.name || 'Unknown Parameter',
            value: param.result || '',
            unit: param.unit || '',
            isAbnormal: param.isAbnormal || false,
            notes: param.notes || '',
            referenceRange: param.referenceRange || {}
          };
        });
      }
    });
  }
  
  console.log('📊 Final initialized data:', initialData);
  setResultsData(initialData);
  setResultsErrors({});
};

// Función para manejar cambios en los resultados
const handleResultChange = (key, field, value) => {
  setResultsData(prev => ({
    ...prev,
    [key]: {
      ...prev[key],
      [field]: value
    }
  }));
  
  // Clear error for this field
  if (resultsErrors[key]?.[field]) {
    setResultsErrors(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: ''
      }
    }));
  }
  
  if (uploadError) {
    setUploadError('');
  }
};

// Función para validar resultados
const validateResults = () => {
  const errors = {};
  let isValid = true;
  
  if (Object.keys(resultsData).length === 0) {
    setUploadError('No parameters found to validate. Please refresh and try again.');
    return false;
  }
  
  Object.keys(resultsData).forEach(key => {
    const result = resultsData[key];
    const fieldErrors = {};
    
    if (!result.value || String(result.value).trim() === '') {
      fieldErrors.value = 'Result value is required';
      isValid = false;
    }
    
    if (Object.keys(fieldErrors).length > 0) {
      errors[key] = fieldErrors;
    }
  });
  
  setResultsErrors(errors);
  
  if (!isValid) {
    const errorCount = Object.keys(errors).length;
    setUploadError(`Please fix ${errorCount} validation error(s) before submitting.`);
  }
  
  return isValid;
};

// Función para enviar resultados
const handleSubmitResults = async () => {
  setUploadError('');
  setUploadSuccess('');
  
  if (!validateResults()) {
    return;
  }
  
  if (!selectedResult.result_id) {
    setUploadError('Result ID is missing. Please refresh and try again.');
    return;
  }
  
  setIsUploadingResults(true);
  
  try {
    const originalOrder = selectedResult._original || selectedResult;
    const testResultsMap = new Map();
    
    // Agrupar parámetros por test
    Object.values(resultsData).forEach(result => {
      const testKey = result.testIndex;
      
      if (!testResultsMap.has(testKey)) {
        const testInfo = originalOrder.selectedTests?.[result.testIndex];
        const testTemplateId = testInfo?.testTemplateId || testInfo?._id;
        
        if (!testTemplateId) {
          throw new Error(`Missing testTemplateId for test: ${result.testName}`);
        }
        
        testResultsMap.set(testKey, {
          testTemplateId: testTemplateId,
          testName: result.testName || testInfo?.testName || testInfo?.name,
          testCode: testInfo?.testCode || testInfo?.code || '',
          category: testInfo?.category || '',
          parameters: [],
          generalNotes: ''
        });
      }
      
      const testInfo = originalOrder.selectedTests?.[result.testIndex];
      const originalParam = testInfo?.selectedParameters?.[result.paramIndex];
      
      testResultsMap.get(testKey).parameters.push({
        parameterId: originalParam?.parameterId || `${testKey}-param-${result.paramIndex}`,
        parameterName: result.parameterName,
        loincCode: originalParam?.loincCode || '',
        value: String(result.value),
        unit: result.unit || originalParam?.unit || '',
        referenceRange: result.referenceRange || originalParam?.referenceRange || {},
        isAbnormal: result.status === 'abnormal' || result.status === 'critical',
        notes: result.notes || ''
      });
    });
    
    const results = Array.from(testResultsMap.values());
    
    const resultsPayload = {
      results: results,
      observations: generalNotes.trim() || '',
      status: 'completed'
    };
    
    const response = await LaboratoryOrdersService.updateOrderResults(
      selectedResult.result_id, 
      resultsPayload
    );
    
    // Update local state
    const updatedResults = results.map(result => {
      if (result.result_id === selectedResult.result_id) {
        return {
          ...result,
          status: 'completed',
          results: results,
          completed_date: new Date().toISOString(),
          results_notes: generalNotes.trim() || null,
          last_updated: new Date().toISOString()
        };
      }
      return result;
    });
    setResults(updatedResults);
    
    setUploadSuccess(`Results uploaded successfully! ${results.length} tests with ${Object.keys(resultsData).length} parameters processed.`);
    
    // Auto-close and refresh
    setTimeout(async () => {
      try {
        await loadResults();
        await loadResultsStats();
      } catch (refreshError) {
        console.warn('Warning: Could not refresh data:', refreshError);
      }
      
      setShowResultsOffcanvas(false);
    }, 2000);
    
  } catch (error) {
    console.error('Error uploading results:', error);
    
    let errorMessage = 'Failed to upload results. ';
    if (error.response?.data) {
      errorMessage += error.response.data.message || 'Server error';
    } else {
      errorMessage += error.message || 'Unknown error occurred';
    }
    
    setUploadError(errorMessage);
  } finally {
    setIsUploadingResults(false);
  }
};

// Función para cerrar modal de upload
const handleCloseResultsOffcanvas = () => {
  if (!isUploadingResults) {
    setShowResultsOffcanvas(false);
    setSelectedResult(null);
    setResultsData({});
    setResultsErrors({});
    setUploadError('');
    setUploadSuccess('');
    setGeneralNotes('');
  }
};

  // **UTILITY FUNCTIONS**
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'high':
        return 'danger';
      case 'medium':
        return 'warning';
      case 'normal':
        return 'success';
      default:
        return 'secondary';
    }
  };

  const getActiveFilters = () => {
    const activeFilters = [];
    
    if (globalFilter) {
      activeFilters.push({
        key: 'search',
        label: `Search: "${globalFilter}"`,
        value: globalFilter,
        type: 'search'
      });
    }
    
    if (filters.testType) {
      activeFilters.push({
        key: 'testType',
        label: `Test Type: ${filters.testType}`,
        value: filters.testType,
        type: 'testType'
      });
    }
    
    if (filters.severity) {
      activeFilters.push({
        key: 'severity',
        label: `Severity: ${filters.severity}`,
        value: filters.severity,
        type: 'severity'
      });
    }
    
    if (filters.abnormalOnly) {
      activeFilters.push({
        key: 'abnormalOnly',
        label: 'Abnormal Only',
        value: filters.abnormalOnly,
        type: 'abnormalOnly'
      });
    }
    
    if (filters.completedDate) {
      activeFilters.push({
        key: 'completedDate',
        label: `From: ${formatDate(filters.completedDate)}`,
        value: filters.completedDate,
        type: 'completedDate'
      });
    }
    
    if (filters.endDate) {
      activeFilters.push({
        key: 'endDate',
        label: `To: ${formatDate(filters.endDate)}`,
        value: filters.endDate,
        type: 'endDate'
      });
    }
    
    if (sortBy !== 'newest') {
      activeFilters.push({
        key: 'sort',
        label: `Sort: ${getSortDisplayText()}`,
        value: sortBy,
        type: 'sort'
      });
    }
    
    return activeFilters;
  };

  const handleRemoveFilter = (filterToRemove) => {
    switch (filterToRemove.type) {
      case 'search':
        setGlobalFilter('');
        break;
      case 'testType':
        setFilters(prev => ({ ...prev, testType: '' }));
        break;
      case 'severity':
        setFilters(prev => ({ ...prev, severity: '' }));
        break;
      case 'abnormalOnly':
        setFilters(prev => ({ ...prev, abnormalOnly: false }));
        break;
      case 'completedDate':
        setFilters(prev => ({ ...prev, completedDate: '' }));
        break;
      case 'endDate':
        setFilters(prev => ({ ...prev, endDate: '' }));
        break;
      case 'sort':
        setSortBy('newest');
        break;
      default:
        break;
    }
  };

  const handleClearAllActiveFilters = () => {
    setGlobalFilter('');
    setFilters({
      testType: '',
      severity: '',
      completedDate: '',
      endDate: '',
      status: 'Completed',
      abnormalOnly: false
    });
    setSortBy('newest');
  };

  // **RENDER FUNCTIONS**
  const renderResultDetails = (result) => {
    const originalOrder = result._original || result;
    
    // Render structured results
    if (originalOrder.results && Array.isArray(originalOrder.results) && originalOrder.results.length > 0) {
      return (
        <div className="results-container">
          <h6 className="text-primary mb-3">Test Results</h6>
          {originalOrder.results.map((testResult, testIndex) => (
            <div key={testIndex} className="test-result-card border rounded p-3 mb-3">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <h6 className="text-primary mb-0">
                  {testResult.testName || `Test ${testIndex + 1}`}
                </h6>
                {testResult.testCode && (
                  <span className="badge bg-secondary">{testResult.testCode}</span>
                )}
              </div>
              
              {testResult.parameters && Array.isArray(testResult.parameters) && (
                <div className="parameters-results">
                  <div className="row g-2">
                    {testResult.parameters.map((param, paramIndex) => (
                      <div key={paramIndex} className="col-md-6">
                        <div className="parameter-result p-2 border rounded bg-light">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <div className="fw-medium">{param.parameterName}</div>
                              <div className="result-value fs-5 text-primary fw-bold">
                                {param.value} {param.unit && <span className="text-muted">{param.unit}</span>}
                              </div>
                              {param.referenceRange && (param.referenceRange.min || param.referenceRange.max) && (
                                <small className="text-muted">
                                  Range: {param.referenceRange.min || 'N/A'} - {param.referenceRange.max || 'N/A'}
                                </small>
                              )}
                            </div>
                            <div className="text-end">
                              {param.isAbnormal ? (
                                <span className="badge bg-warning">Abnormal</span>
                              ) : (
                                <span className="badge bg-success">Normal</span>
                              )}
                            </div>
                          </div>
                          {param.notes && (
                            <div className="mt-2">
                              <small className="text-info">
                                <FiInfo className="me-1" />
                                {param.notes}
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {testResult.generalNotes && (
                <div className="mt-2 p-2 bg-info bg-opacity-10 rounded">
                  <small className="text-info">
                    <FiFileText className="me-1" />
                    {testResult.generalNotes}
                  </small>
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }
    
    // Fallback: Check if selectedTests have results
    if (originalOrder.selectedTests && Array.isArray(originalOrder.selectedTests)) {
      return (
        <div className="results-container">
          <h6 className="text-primary mb-3">Test Results</h6>
          {originalOrder.selectedTests.map((test, testIndex) => {
            const hasTestResults = test.selectedParameters && test.selectedParameters.some(param => 
              param.result !== undefined && param.result !== null && param.result !== ''
            );
            
            if (!hasTestResults) return null;
            
            return (
              <div key={testIndex} className="test-result-card border rounded p-3 mb-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <h6 className="text-primary mb-0">
                    {test.testName || test.name || `Test ${testIndex + 1}`}
                  </h6>
                  {test.testCode && (
                    <span className="badge bg-secondary">{test.testCode}</span>
                  )}
                </div>
                
                <div className="parameters-results">
                  <div className="row g-2">
                    {test.selectedParameters?.filter(param => 
                      param.result !== undefined && param.result !== null && param.result !== ''
                    ).map((param, paramIndex) => (
                      <div key={paramIndex} className="col-md-6">
                        <div className="parameter-result p-2 border rounded bg-light">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <div className="fw-medium">{param.parameterName || param.name}</div>
                              <div className="result-value fs-5 text-primary fw-bold">
                                {param.result} {param.unit && <span className="text-muted">{param.unit}</span>}
                              </div>
                              {param.referenceRange && (param.referenceRange.min || param.referenceRange.max) && (
                                <small className="text-muted">
                                  Range: {param.referenceRange.min || 'N/A'} - {param.referenceRange.max || 'N/A'}
                                </small>
                              )}
                            </div>
                            <div className="text-end">
                              {param.isAbnormal ? (
                                <span className="badge bg-warning">Abnormal</span>
                              ) : (
                                <span className="badge bg-success">Normal</span>
                              )}
                            </div>
                          </div>
                          {param.notes && (
                            <div className="mt-2">
                              <small className="text-info">
                                <FiInfo className="me-1" />
                                {param.notes}
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }
    
    // Final fallback
    return (
      <div className="alert alert-info">
        <FiInfo className="me-2" />
        <strong>Results Completed</strong>
        <p className="mb-0 mt-2">
          This order has been marked as completed but detailed results are not available in the current format.
        </p>
      </div>
    );
  };

  // Loading state
  if (isLoading && patientId) {
    return <div>Loading laboratory results...</div>;
  }

  const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return 'success';
    case 'in_progress':
    case 'processing':
      return 'warning';
    case 'pending':
      return 'secondary';
    case 'cancelled':
      return 'danger';
    case 'critical':
      return 'danger';
    default:
      return 'primary';
  }
};

const getStatusIcon = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return <FiCheck />;
    case 'in_progress':
    case 'processing':
      return <FiActivity />;
    case 'pending':
      return <FiClock />;
    case 'cancelled':
      return <FiX />;
    case 'critical':
      return <FiAlertTriangle />;
    default:
      return <FiActivity />;
  }
};

  return (
    <main id="app">
      <TopHeader />
      <div className="container-fluid w-100">
        <PatientManagement activeMenu='/my-results'/>
        
        {/* Breadcrumb */}
        <div className="mb-3">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><a href="/dashboard">Dashboard</a></li>
              <li className="breadcrumb-item"><a href="/patients/list">Patients</a></li>
              <li className="breadcrumb-item active" aria-current="page">
                My Results {patientData?.patientName ? `- ${patientData.patientName}` : ''}
              </li>
            </ol>
          </nav>
        </div>

        <div className="card-body">
          {/* Enhanced Search and Actions Bar */}
          <div className="row mb-3">
            <div className="col-12">
              <div className="d-flex align-items-center justify-content-between">
                
                {/* Left side - Search */}
                <div className="d-flex align-items-center gap-2">
                  <div className="input-group" style={{maxWidth: '300px'}}>
                    <span className="input-group-text">
                      <FiSearch />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search results"
                      value={globalFilter}
                      onChange={(e) => handleGlobalFilterChange(e.target.value)}
                    />
                  </div>
                </div>

                {/* Right side - Controls */}
                <div className="d-flex align-items-center gap-2">
                  {/* Sort By Dropdown */}
                  <div className="dropdown" style={{position: 'relative'}}>
                    <button
                      className="btn btn-outline-secondary d-flex align-items-center gap-2"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSortDropdown(!showSortDropdown);
                      }}
                    >
                      {getSortDisplayText()}
                      <FiChevronDown />
                    </button>
                    {showSortDropdown && (
                      <div className="dropdown-menu show" style={{position: 'absolute', top: '100%', right: 0, zIndex: 1000}}>
                        <button
                          className={`dropdown-item ${sortBy === 'newest' ? 'active' : ''}`}
                          onClick={() => handleSortChange('newest')}
                        >
                          Newest First
                        </button>
                        <button
                          className={`dropdown-item ${sortBy === 'oldest' ? 'active' : ''}`}
                          onClick={() => handleSortChange('oldest')}
                        >
                          Oldest First
                        </button>
                        <button
                          className={`dropdown-item ${sortBy === 'severity' ? 'active' : ''}`}
                          onClick={() => handleSortChange('severity')}
                        >
                          By Severity
                        </button>
                        <button
                          className={`dropdown-item ${sortBy === 'abnormal' ? 'active' : ''}`}
                          onClick={() => handleSortChange('abnormal')}
                        >
                          Most Abnormal
                        </button>
                        <button
                          className={`dropdown-item ${sortBy === 'alphabetical' ? 'active' : ''}`}
                          onClick={() => handleSortChange('alphabetical')}
                        >
                          A-Z
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Filter Button */}
                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={handleOpenFilterOffcanvas}
                  >
                    <FiFilter />
                    Filter
                  </button>
                  {/* Add New Button */}
                <button
                  className="btn btn-primary addnewbtn d-flex gap-2"
                  onClick={handleAddNew}
                >
                  + Add New
                </button>
                </div>
              </div>
            </div>
          </div>

          {/* Active Filters Display */}
          {getActiveFilters().length > 0 && (
            <div className="row mb-3">
              <div className="col-12">
                <div className="d-flex align-items-center gap-2 p-3 bg-light rounded">
                  <span className="text-muted fw-medium">Active filters:</span>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    {getActiveFilters().map((filter) => (
                      <span
                        key={filter.key}
                        className="badge bg-primary d-flex align-items-center gap-1"
                        style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}
                      >
                        {filter.label}
                        <button
                          type="button"
                          className="btn-close btn-close-white"
                          style={{ 
                            fontSize: '0.625rem',
                            padding: '0.125rem',
                            marginLeft: '0.25rem'
                          }}
                          onClick={() => handleRemoveFilter(filter)}
                          title={`Remove ${filter.label} filter`}
                        ></button>
                      </span>
                    ))}
                    
                    {/* Clear All Button */}
                    {getActiveFilters().length > 1 && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                        onClick={handleClearAllActiveFilters}
                        title="Clear all filters"
                      >
                        <FiX size={14} />
                        Clear All
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          

          {/* Main Table */}
          <MaterialReactTable
            columns={columns}
            data={filteredResults}
            
            // Basic configuration
            enableRowSelection
            enableColumnFilters
            enableGlobalFiltering
            enableSorting={false}
            enablePagination
            
            // Toolbars
            enableTopToolbar
            enableBottomToolbar
            
            // Features
            enableDensityToggle
            enableFullScreenToggle
            enableHiding
            enableColumnOrdering
            enableColumnResizing={false}
            enableGrouping={false}
            
            // States
            initialState={{
              pagination: { pageSize: 10, pageIndex: 0 },
              sorting: [{ id: 'completed_date', desc: true }],
              density: 'comfortable',
              showGlobalFilter: false
            }}
            
            state={{
              isLoading,
              rowSelection,
              globalFilter
            }}
            
            // Callbacks
            onRowSelectionChange={setRowSelection}
            onGlobalFilterChange={handleGlobalFilterChange}
            
            // Rendering
            muiTableContainerProps={{
              sx: { maxHeight: '600px' }
            }}
            
            muiTableBodyRowProps={({ row }) => ({
              onClick: () => handleViewResult(row.original.result_id),
              sx: {
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#f8f9fa'
                }
              }
            })}
          />

          {/* Results Summary */}
          {filteredResults.length > 0 && (
            <div className="mt-3">
              <small className="text-muted">
                Showing {filteredResults.length} of {results.length} results
                {Object.values(filters).some(f => f && f !== 'Completed') || globalFilter ? ' (filtered)' : ''}
              </small>
            </div>
          )}

          {/* No Results Message */}
          {results.length === 0 && !isLoading && (
            <div className="text-center py-5">
              <FiFileText size={48} className="text-muted mb-3" />
              <h5 className="text-muted">No Results Available</h5>
              <p className="text-muted">
                {patientData?.patientName ? 
                  `${patientData.patientName} has no completed laboratory results yet.` :
                  'No completed laboratory results found for this patient.'
                }
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Filter Offcanvas */}
      {showFilterOffcanvas && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={handleCloseFilterOffcanvas}></div>
          <div className="offcanvas offcanvas-end show" tabIndex="-1">
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Filter Results</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseFilterOffcanvas}
              ></button>
            </div>
            <div className="offcanvas-body">
              <form>
                {/* Test Type Filter */}
                <div className="mb-3">
                  <label className="form-label">Test Type</label>
                  <input
                    type="text"
                    className="form-control"
                    value={filters.testType}
                    onChange={(e) => handleFilterChange('testType', e.target.value)}
                    placeholder="e.g., Blood Chemistry, Hematology"
                  />
                </div>

                {/* Severity Filter */}
                <div className="mb-3">
                  <label className="form-label">Severity</label>
                  <select
                    className="form-select"
                    value={filters.severity}
                    onChange={(e) => handleFilterChange('severity', e.target.value)}
                  >
                    <option value="">All Severities</option>
                    <option value="normal">Normal</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>

                <div className="mb-3">
  <label className="form-label">Status</label>
  <select
    className="form-select"
    value={filters.status}
    onChange={(e) => handleFilterChange('status', e.target.value)}
  >
    <option value="">All Statuses</option>
    <option value="pending">Pending</option>
    <option value="in_progress">In Progress</option>
    <option value="completed">Completed</option>
    <option value="cancelled">Cancelled</option>
  </select>
</div>

                {/* Abnormal Only Filter */}
                <div className="mb-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={filters.abnormalOnly}
                      onChange={(e) => handleFilterChange('abnormalOnly', e.target.checked)}
                      id="abnormalOnlyCheck"
                    />
                    <label className="form-check-label" htmlFor="abnormalOnlyCheck">
                      Show only results with abnormal values
                    </label>
                  </div>
                </div>

                {/* Date Range Filters */}
                <div className="mb-3">
                  <label className="form-label">Completed From</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.completedDate}
                    onChange={(e) => handleFilterChange('completedDate', e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Completed To</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  />
                </div>

                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-primary flex-fill"
                    onClick={handleApplyFilters}
                  >
                    Apply Filters
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary flex-fill"
                    onClick={handleClearFilters}
                  >
                    Clear All
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* Add New Order Offcanvas */}
              {showAddOffcanvas && (
                <>
                  <div className="offcanvas-backdrop fade show" onClick={handleCloseAddOffcanvas}></div>
                  <div className="offcanvas offcanvas-end show" style={{visibility: 'visible', width: '600px'}}>
                    <div className="offcanvas-header">
                      <h5 className="offcanvas-title">Add New Laboratory Order</h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={handleCloseAddOffcanvas}
                        disabled={isSubmitting}
                      ></button>
                    </div>
                    <div className="offcanvas-body">
                      <AddNewLaboratoryOrder
                        patientId={patientId}
                        onSubmit={handleSubmitNewOrder}
                        onCancel={handleCloseAddOffcanvas}
                        isSubmitting={isSubmitting}
                      />
                    </div>
                  </div>
                </>
              )}

      {/* View Result Details Offcanvas */}
      {showViewOffcanvas && selectedResult && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={handleCloseViewOffcanvas}></div>
          <div className="offcanvas offcanvas-end show" tabIndex="-1" style={{width: '800px'}}>
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Laboratory Results</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseViewOffcanvas}
              ></button>
            </div>
            <div className="offcanvas-body">
              <div className="row g-4">
                {/* Order Information */}
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="card-title mb-0">Order Information</h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Order Number</label>
                          <div className="form-control-plaintext">
                            {selectedResult.order_number || 'N/A'}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Sample ID</label>
                          <div className="form-control-plaintext">
                            {selectedResult.sample_id || 'Not assigned'}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Status</label>
                          <div>
                            <span className="badge bg-success d-flex align-items-center gap-1 w-auto">
                              <FiCheck />
                              Completed
                            </span>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Results Status</label>
                          <div>
                            {selectedResult.has_abnormal ? (
                              <span className={`badge bg-${selectedResult.severity === 'high' ? 'danger' : 'warning'} d-flex align-items-center gap-1 w-auto`}>
                                <FiAlertTriangle />
                                {selectedResult.abnormal_count} Abnormal Values
                              </span>
                            ) : (
                              <span className="badge bg-success d-flex align-items-center gap-1 w-auto">
                                <FiCheck />
                                All Normal
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Order Date</label>
                          <div className="form-control-plaintext">
                            {formatDateTime(selectedResult.order_date)}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Completed Date</label>
                          <div className="form-control-plaintext">
                            {formatDateTime(selectedResult.completed_date)}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Ordered By</label>
                          <div className="form-control-plaintext">
                            {selectedResult.ordered_by || 'Unknown'}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Processed By</label>
                          <div className="form-control-plaintext">
                            {selectedResult.processed_by || 'Lab'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Results Section */}
                <div className="col-12">
                  <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h6 className="card-title mb-0">
                        <FiActivity className="me-2 text-primary" />
                        Test Results
                      </h6>
                      <span className="badge bg-info">
                        {selectedResult.total_parameters} total parameters
                      </span>
                    </div>
                    <div className="card-body">
                      {renderResultDetails(selectedResult)}
                      
                      {/* General results notes */}
                      {selectedResult.results_notes && (
                        <div className="mt-3 p-3 bg-light rounded">
                          <h6 className="text-primary mb-2">
                            <FiFileText className="me-2" />
                            Additional Notes
                          </h6>
                          <p className="mb-0">{selectedResult.results_notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="col-12">
                  <div className="d-flex gap-2 justify-content-end">
                    <button
                      className="btn btn-success"
                      onClick={() => handleDownloadResults(selectedResult.result_id)}
                    >
                      <FiDownload className="me-2" />
                      Download Results
                    </button>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={handleCloseViewOffcanvas}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {/* Upload Results Offcanvas */}
{showResultsOffcanvas && selectedResult && (
  <>
    <div className="offcanvas-backdrop fade show" onClick={handleCloseResultsOffcanvas}></div>
    <div className="offcanvas offcanvas-end show" style={{visibility: 'visible', width: '700px'}}>
      <div className="offcanvas-header">
        <h5 className="offcanvas-title">Upload Results</h5>
        <button
          type="button"
          className="btn-close"
          onClick={handleCloseResultsOffcanvas}
          disabled={isUploadingResults}
        ></button>
      </div>
      <div className="offcanvas-body">
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="text-primary mb-0">
              Order: {selectedResult.order_number}
            </h6>
            <span className={`badge bg-${getStatusColor(selectedResult.status)}`}>
              {selectedResult.status}
            </span>
          </div>
          <small className="text-muted">
            Patient: {selectedResult.patient_name || 'Unknown'} 
            {selectedResult.patient_id && ` (ID: ${selectedResult.patient_id})`}
          </small>
        </div>

        {/* Success/Error Messages */}
        {uploadSuccess && (
          <div className="alert alert-success d-flex align-items-center">
            <FiCheck className="me-2" />
            {uploadSuccess}
          </div>
        )}
        
        {uploadError && (
          <div className="alert alert-danger d-flex align-items-center">
            <FiAlertTriangle className="me-2" />
            {uploadError}
          </div>
        )}

        {/* Results Form */}
        <div className="row g-3">
          {Object.keys(resultsData).length === 0 ? (
            <div className="col-12">
              <div className="alert alert-warning">
                <FiAlertTriangle className="me-2" />
                No parameters found for this order. Please refresh and try again.
              </div>
            </div>
          ) : (
            Object.entries(resultsData).map(([key, result]) => (
              <div key={key} className="col-12">
                <div className="card">
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-12">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <h6 className="text-primary mb-1">{result.testName}</h6>
                            <div className="fw-medium">{result.parameterName}</div>
                          </div>
                          {result.unit && (
                            <span className="badge bg-light text-dark border">
                              {result.unit}
                            </span>
                          )}
                        </div>
                        
                        {result.referenceRange && (result.referenceRange.min || result.referenceRange.max) && (
                          <div className="mb-2">
                            <small className="text-muted">
                              <FiInfo className="me-1" />
                              Reference Range: {result.referenceRange.min || 'N/A'} - {result.referenceRange.max || 'N/A'}
                            </small>
                          </div>
                        )}
                      </div>
                      
                      <div className="col-md-8">
                        <label className="form-label">Result Value</label>
                        <input
                          type="text"
                          className="form-control"
                          value={result.value || ''}
                          onChange={(e) => handleResultChange(key, 'value', e.target.value)}
                          placeholder="Enter result value"
                          disabled={isUploadingResults}
                        />
                      </div>
                      
                      <div className="col-md-4">
                        <label className="form-label">Status</label>
                        <select
                          className="form-select"
                          value={result.status || 'normal'}
                          onChange={(e) => handleResultChange(key, 'status', e.target.value)}
                          disabled={isUploadingResults}
                        >
                          <option value="normal">Normal</option>
                          <option value="abnormal">Abnormal</option>
                          <option value="critical">Critical</option>
                          <option value="pending">Pending</option>
                        </select>
                      </div>
                      
                      <div className="col-12">
                        <label className="form-label">Notes (Optional)</label>
                        <textarea
                          className="form-control"
                          rows="2"
                          value={result.notes || ''}
                          onChange={(e) => handleResultChange(key, 'notes', e.target.value)}
                          placeholder="Additional notes or observations"
                          disabled={isUploadingResults}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
          
          {/* General Results Notes */}
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h6 className="card-title mb-0">General Notes</h6>
              </div>
              <div className="card-body">
                <textarea
                  className="form-control"
                  rows="3"
                  value={generalNotes}
                  onChange={(e) => setGeneralNotes(e.target.value)}
                  placeholder="General observations, recommendations, or additional notes about the results"
                  disabled={isUploadingResults}
                />
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="col-12">
            <div className="d-flex gap-2 justify-content-end pt-3 border-top">
              <button
                className="btn btn-outline-secondary"
                onClick={handleCloseResultsOffcanvas}
                disabled={isUploadingResults}
              >
                Cancel
              </button>
              <button
                className="btn btn-success"
                onClick={handleSubmitResults}
                disabled={isUploadingResults || Object.keys(resultsData).length === 0}
              >
                {isUploadingResults ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Uploading...
                  </>
                ) : (
                  <>
                    <FiCheck className="me-2" />
                    Submit Results
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </>
)}

      {/* Patient Clinical Data Component */}
      <PatientMedicalClinicalData 
        patientData={patientData}
        alertInfo={alertInfo}
      />
    </main>
  );
};

export default ResultTreatments;